import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { ImageModule } from '@df/module-image/image.module';
import { Basket } from '@df/session/api/basket/basket';
import { BulkOrder } from '@df/session/api/basket/bulk-order/bulk-order';
import { AttributeLabelPipeService } from '@df/ui/common/attribute-label.pipe';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { HideUpComponent } from '@df/ui/hide-up/hide-up.component';
import { PriceComponent } from '@df/ui/price/price.component';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import uniq from 'lodash-es/uniq';
import { BehaviorSubject } from 'rxjs';
import { ItemQtyComponent } from '../../basket/item-qty.component';
import { LocalProduct } from '../../catalog/model/product/product.decorator';
@Component({
    selector: 'bulk-order-listing-product',
    templateUrl: 'bulk-order-listing-product.html',
    standalone: true,
    imports: [
        HrefComponent,
        ImageModule,
        SizeClassDirective,
        OptionLabelPipe,
        TranslationModule,
        PriceComponent,
        ToggleModule,
        NgTemplateOutlet,
        ItemQtyComponent,
        HideUpComponent,
        BulkOrderListingProductComponent,
        NgClass
    ],
    styleUrls: ['bulk-order-listing-product.scss']
})
export class BulkOrderListingProductComponent extends UiComponent implements OnInit, OnDestroy {
    @Input({
        required: true
    })
    product!: LocalProduct<IProductDataV2>;

    @Input({
        required: true
    })
    bulk!: BulkOrder;

    @Input()
    childMode = false;

    protected children: LocalProduct<IProductDataV2>[] = [];
    protected childrenOpen$ = new BehaviorSubject(false);

    private basket = inject(Basket);
    private attributeLabelPipeService = inject(AttributeLabelPipeService);

    override ngOnInit() {
        this.subscriptions.add(this.basket.subscribe(() => this.changeDetectorRef.detectChanges()));
    }

    override ngOnChanges(changes: SimpleChanges) {
        if (changes['product'] && this.product?.isConfigurable) {
            const directChildren = this.product.directChildren.filter(i => i.data) as LocalProduct<IProductDataV2>[];
            if (!directChildren.length) {
                return;
            }
            this.children = uniq(
                this.product
                    .getConfigurableAttributeOptions('size')
                    .map(item => {
                        return directChildren.find(i => (i.data as IProductDataV2).size === item.value);
                    })
                    .filter(i => !!i)
            );
        }
        super.ngOnChanges(changes);
    }

    protected get isBackOrderOnlyUnlessParent(): boolean {
        return this.product.isBackorderOnly() && !this.product.directParent?.isBackorderOnly();
    }

    get isParent(): boolean {
        return !this.product.data!.parent && !this.product.isSimple;
    }

    get hideParentQty(): boolean {
        return this.isParent && this.bulk.getQty(this.product) === 0;
    }

    getExpandLabel(): string | undefined {
        if (this.product.isConfigurable) {
            const attrs = this.product.configurableAttributeCodes;
            if (attrs?.length === 1) {
                return this.attributeLabelPipeService.execute(attrs[0]!)!.toLowerCase() + 's';
            }
            return 'sizes';
        }
    }

    getProductInformationClasses(): string {
        let classes = '';
        if (this.isMobileConfigurable) {
            classes = 'w-8 flex-grow';
        } else {
            classes = 'c-3 m-b-0';
            if (!this.childMode) {
                classes = classes + ' c-5-s';
            }
        }
        if (!this.childMode && this.client.isS) {
            classes = classes + ' flex-column flex-justify-between';
        }
        return classes;
    }
    get isMobileConfigurable(): boolean {
        return this.client.isS && this.product.isConfigurable;
    }
    /**
     * Take configurable attrubute codes, but skip ones that include cross-sell linking magic
     */
    getConfigurableCodes(product: Product) {
        const codes = product.parent.configurableAttributeCodes;
        if (codes && codes.length > 0) {
            const directChildren = product.parent.directChildren.filter(i => i.data);
            return codes.filter(code => uniq(directChildren.map(i => i.data![code])).length > 0 && code !== 'color');
        }
    }

    protected toggle(to = !this.childrenOpen$.value) {
        this.childrenOpen$.next(to);
    }
}
